import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Icon,
  IconButton,
  CircularProgress,
  alpha,
} from "@mui/material";

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  itemName: string;
  itemDescription?: string;
  loading?: boolean;
  icon?: string;
}

export const DeleteConfirmationDialog: React.FC<
  DeleteConfirmationDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  itemDescription,
  loading = false,
  icon = "delete",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          background: "#DC2626",
          color: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Icon sx={{ fontSize: 20 }}>{icon}</Icon>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
            textAlign: "center",
            py: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: alpha("#DC2626", 0.08),
              color: "#DC2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Icon sx={{ fontSize: 32 }}>{icon}</Icon>
          </Box>
          <Typography
            variant="h6"
            sx={{ color: "text.primary", fontFamily: "Montserrat" }}
          >
            Are you sure you want to delete this {title.toLowerCase()}?
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              fontFamily: "Montserrat",
            }}
          >
            {itemName}
          </Typography>
          {itemDescription && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                maxWidth: 400,
                fontFamily: "Montserrat",
              }}
            >
              {itemDescription}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              color: "#DC2626",
              mt: 2,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontFamily: "Montserrat",
            }}
          >
            <Icon sx={{ fontSize: 16 }}>warning</Icon>
            This action cannot be undone
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          bgcolor: "#F8FAFC",
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          startIcon={<Icon>close</Icon>}
          disabled={loading}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            px: 2,
            py: 1,
            borderColor: "#E5E7EB",
            color: "#64748B",
            fontFamily: "Montserrat",
            "&:hover": {
              borderColor: "#94A3B8",
              bgcolor: "transparent",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Icon>delete_forever</Icon>
            )
          }
          sx={{
            borderRadius: 1,
            textTransform: "none",
            px: 3,
            py: 1,
            bgcolor: "#DC2626",
            fontFamily: "Montserrat",
            "&:hover": {
              bgcolor: "#B91C1C",
            },
            "&.Mui-disabled": {
              bgcolor: alpha("#DC2626", 0.6),
              color: "white",
            },
          }}
        >
          {loading ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
